import type { SharedView as SharedPayload } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { api, ApiError } from '../lib/api.js';
import { SharedPage } from './SharedView.js';
import { SharedDocument } from './Documents.js';
import { documentForRole } from '@adhd/shared';

/**
 * "See what they see": a link's page exactly as whoever holds it gets it, now.
 *
 * The server builds it with the same function the link itself uses, and this
 * renders it with the same component, so what the owner checks here cannot
 * drift from what they read. Opening it here is not an opening: no access-log
 * row, no count, and nothing written — the same read-only path as the link.
 */
export function SharePreview(): JSX.Element {
  const { id, doc } = useParams<{ id: string; doc?: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<SharedPayload | null>(null);
  const [off, setOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setView(null);
    setOff(false);
    setError(null);
    if (!id) {
      setError('No such link.');
      return;
    }
    api
      .sharePreview(id)
      .then(setView)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409) {
          setOff(true);
          return;
        }
        setError(e instanceof ApiError ? e.message : "Couldn't load that link.");
      });
  }, [id]);

  const back = (
    <button
      className="btn btn-ghost btn-compact"
      style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
      onClick={() => navigate('/circle')}
    >
      <ArrowLeftIcon /> Back to Circle
    </button>
  );

  if (off) {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          {back}
          <h1>This link is switched off</h1>
          <p>They see only “That link is not valid.”</p>
        </header>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stack-lg stack">
        <div>{back}</div>
        <div className="banner banner-error">{error}</div>
      </div>
    );
  }

  if (!view) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="stack-lg stack">
      <div>{back}</div>
      <div className="banner banner-caution">
        This is what {view.label} sees right now. Opening it here is not recorded as an opening and
        makes the app write nothing new.
      </div>
      {/* Exactly what they open: the document first where the role has one,
          the whole page behind it (…/preview/page). */}
      {doc === 'brief' || doc === 'week' || doc === 'card' || (doc === undefined && documentForRole(view.role)) ? (
        <SharedDocument view={view} kind={doc === 'brief' || doc === 'week' || doc === 'card' ? doc : documentForRole(view.role)!} pageHref={`/share/${id}/preview/page`} />
      ) : (
        <SharedPage view={view} docHref={documentForRole(view.role) ? `/share/${id}/preview/${documentForRole(view.role)}` : undefined} />
      )}
    </div>
  );
}
